import * as React from 'react';
import clsx from 'clsx';
import {
	FieldError,
	FieldErrorsImpl,
	FieldValues,
	Merge,
	UseFormRegister,
	UseFormSetValue,
	RegisterOptions,
} from 'react-hook-form';
import { CircleCheck } from 'lucide-react';
import { Typography } from '@/components/common';
import { InputPhone } from '@/components/form';
import { ModalVerification } from './ModalVerification';
import { verificationsService } from '@/api/verifications';
import { useNotification } from '@/hooks';
import { formatErrorMessage, formatPhoneRemove } from '@/lib';
import { ErrorHttpResponse } from '@/interfaces';

interface Props {
	error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	isPhoneVerified?: boolean;
	phoneNumber: string;
	initialPhoneValue: string;
	refetch: () => void;
	rules?: RegisterOptions;
	showVerifyOptions?: boolean;
	classNameInput?: string;
}

export const FormInputPhone: React.FC<Props> = ({
	register,
	refetch,
	error,
	setValue,
	isPhoneVerified = false,
	phoneNumber,
	initialPhoneValue,
	rules,
	showVerifyOptions,
	classNameInput,
}) => {
	const { toastSuccess, toastError } = useNotification();
	const [isVerifyingPhone, setIsVerifyingPhone] =
		React.useState<boolean>(false);
	const [errorVerification, setErrorVerification] = React.useState<
		string | React.ReactNode
	>('');

	const validatePhoneNumber = async (): Promise<void> => {
		try {
			await verificationsService.verifyNumber('phone_verification');
			toastSuccess('Verification code has been successfully sent.');
			setIsVerifyingPhone(true);
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const validateOtpCode = async (code: string): Promise<void> => {
		try {
			await verificationsService.validatePhoneOtp(code, 'phone_verification');
			toastSuccess('Your phone number has been successfully verified.');
			refetch();
			setIsVerifyingPhone(false);
		} catch (error) {
			const status = (error as ErrorHttpResponse).statusCode;
			if (!status) {
				toastError(formatErrorMessage(undefined));
				setIsVerifyingPhone(false);
			} else if (status === 400) {
				setErrorVerification(
					<span>
						The verification code has expired. <br />
						Please request a new one and try again.
					</span>
				);
			} else {
				setErrorVerification(
					'The verification code is incorrect. Please try again.'
				);
			}
		}
	};

	const sentCodeAgain = async (): Promise<void> => {
		try {
			await verificationsService.verifyNumber('phone_verification');
			toastSuccess('Verification code has been successfully sent.');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const lastTwoDigits = String(phoneNumber)?.slice(-2);
	const phoneNumberFormatted = formatPhoneRemove(phoneNumber);

	React.useEffect(() => {
		// Seed only an empty field. Try Again remounts this input while the
		// form still holds the typed number; writing initialPhoneValue here
		// would put the saved profile phone back.
		if (initialPhoneValue && !formatPhoneRemove(phoneNumber || '')) {
			setValue('phone', initialPhoneValue);
		}
		// phoneNumber is read when the saved phone arrives, not on each keystroke.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialPhoneValue]);

	return (
		<>
			<div className="flex flex-col items-start">
				<div className="relative w-full">
					<InputPhone
						name="phone"
						register={register}
						placeholder="Phone number"
						className={clsx('w-full md:w-[500px]', classNameInput)}
						classNameInput="!mt-0"
						setValueInput={setValue}
						error={error}
						rules={rules}
					/>
					{showVerifyOptions && (
						<Typography
							type="regular-12"
							className={clsx(
								'absolute top-6 right-5 items-center gap-1 text-green-130',
								{
									flex:
										isPhoneVerified &&
										phoneNumberFormatted === initialPhoneValue,
									hidden:
										!isPhoneVerified ||
										phoneNumberFormatted !== initialPhoneValue ||
										!phoneNumber,
								}
							)}
						>
							Verified
							<CircleCheck className="w-4 h-4 text-green-130" />
						</Typography>
					)}
				</div>
				{showVerifyOptions && (
					<button
						className={clsx({
							hidden:
								isPhoneVerified ||
								!phoneNumber ||
								error ||
								initialPhoneValue !== phoneNumber,
						})}
						type="button"
						onClick={validatePhoneNumber}
					>
						<Typography
							type="semibold-16"
							text="Verify your phone number"
							className="text-purple-150 px-4 py-2"
						/>
					</button>
				)}
			</div>

			<ModalVerification
				title="Verify your phone"
				description={
					<div>
						<Typography type="regular-16" className="opacity-60">
							{`We've sent a code to your phone number ending in`}
						</Typography>
						<Typography type="regular-16" className="opacity-60">
							{`XXXXXXX${lastTwoDigits}`}
						</Typography>
					</div>
				}
				sentCodeAgain={sentCodeAgain}
				isOpen={isVerifyingPhone}
				setIsOpen={setIsVerifyingPhone}
				setIsModalOpen={setIsVerifyingPhone}
				error={errorVerification}
				setErrorVerification={setErrorVerification}
				validateOtpCode={validateOtpCode}
				numDigits={4}
			/>
		</>
	);
};
